import { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useGetCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useGetCreditCardApplicationQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { useFlowType } from "@lending/hooks/useFlowType";
import { getCurrentFlowStepId, getStepPath } from "@lending/navigation";
import { useStore } from "../../contexts/StoreContext";
import { getAutoLoanCanonicalRoutePolicy } from "../layouts/autoLoanRoutePolicy";
import { getCreditCardCanonicalRoutePolicy } from "../layouts/creditCardRoutePolicy";
import { getPersonalLoanCanonicalRoutePolicy } from "../layouts/personalLoanRoutePolicy";
import type { paths } from "../../types/schema-lending";
import type { ProductType } from "@lending/navigation";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type AutoLoanApplication =
  paths["/v2/lending/vehicle_loan_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"];
type CreditCardApplication =
  paths["/v2/lending/credit_card_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"];
type PersonalLoanApplication =
  paths["/v2/lending/personal_loan_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"];

interface AdditionalField {
  name: string;
  value: boolean;
}

function isAutoRequestedAmountEnabled(additionalFields: AdditionalField[] | undefined) {
  return (
    additionalFields?.some((field) => field.name === "auto_requested_loan_amount" && field.value) ??
    false
  );
}

interface ExistingApplicationGuardProps {
  productType: ProductType;
  allowedPages: string[];
  children?: ReactNode;
}

function ExistingApplicationGuard({
  productType,
  allowedPages,
  children,
}: ExistingApplicationGuardProps) {
  const location = useLocation();
  const flowType = useFlowType();
  const { data: storeData } = useStore();
  const { institution } = useGetInstitutionQuery();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const currentPage = pathSegments[pathSegments.length - 1] ?? "";
  const [, maybeApplicationUuid] = pathSegments;
  const hasScopedApplicationPath =
    Boolean(maybeApplicationUuid) && UUID_PATTERN.test(maybeApplicationUuid);
  const hasMatchingStoredProductType =
    storeData.productType === undefined || storeData.productType === productType;
  const shouldResolveExistingApplicationRoute =
    Boolean(storeData.lendingApplicationID) &&
    hasMatchingStoredProductType &&
    !allowedPages.includes(currentPage) &&
    !hasScopedApplicationPath;

  const creditCardQuery = useGetCreditCardApplicationQuery({
    applicationUuid: storeData.lendingApplicationID,
    enabled: shouldResolveExistingApplicationRoute && productType === "credit-card",
  });
  const personalLoanQuery = useGetPersonalLoanApplicationQuery({
    applicationUuid: storeData.lendingApplicationID,
    enabled: shouldResolveExistingApplicationRoute && productType === "personal-loan",
  });
  const autoLoanQuery = useGetVehicleLoanApplicationQuery({
    applicationUuid: storeData.lendingApplicationID,
    enabled: shouldResolveExistingApplicationRoute && productType === "auto-loan",
  });

  const query =
    productType === "credit-card"
      ? creditCardQuery
      : productType === "personal-loan"
        ? personalLoanQuery
        : autoLoanQuery;
  const application = query.data;
  const isLoading = query.isLoading;
  const isFetching = query.isFetching;
  const autoLoanApplication =
    productType === "auto-loan" ? (application as AutoLoanApplication) : null;
  const isAutoRequestedAmountActive =
    productType === "auto-loan" &&
    isAutoRequestedAmountEnabled(institution.lending_ask_additional_fields) &&
    autoLoanApplication?.has_vehicle_selected === false &&
    autoLoanApplication?.is_refinance === false &&
    autoLoanApplication?.requested_amount_cents == null;

  if (shouldResolveExistingApplicationRoute && !application && (isLoading || isFetching)) {
    return null;
  }

  if (shouldResolveExistingApplicationRoute && application && storeData.lendingApplicationID) {
    const currentStepId = getCurrentFlowStepId(
      location.pathname,
      productType,
      flowType,
      storeData.lendingApplicationID
    );
    const canonicalStepId =
      productType === "credit-card"
        ? getCreditCardCanonicalRoutePolicy(
            application as CreditCardApplication,
            currentStepId,
            undefined,
            flowType
          ).canonicalStepId
        : productType === "personal-loan"
          ? getPersonalLoanCanonicalRoutePolicy(
              application as PersonalLoanApplication,
              currentStepId,
              undefined,
              flowType
            ).canonicalStepId
          : getAutoLoanCanonicalRoutePolicy(
              application as AutoLoanApplication,
              currentStepId,
              undefined,
              flowType,
              isAutoRequestedAmountActive
            ).canonicalStepId;

    if (canonicalStepId) {
      const navigationContext =
        productType === "auto-loan" && autoLoanApplication
          ? {
              applicationState: autoLoanApplication.state,
              hasVehicleSelected: autoLoanApplication.has_vehicle_selected,
              isRefinance: autoLoanApplication.is_refinance,
              isAutoRequestedAmountEnabled: isAutoRequestedAmountEnabled(
                institution.lending_ask_additional_fields
              ),
            }
          : { applicationState: application.state };

      const canonicalPath = getStepPath(
        canonicalStepId,
        productType,
        flowType,
        navigationContext,
        storeData.lendingApplicationID
      );

      if (canonicalPath && canonicalPath !== location.pathname) {
        return <Navigate to={`${canonicalPath}${location.search}`} replace />;
      }
    }
  }

  return children ? <>{children}</> : <Outlet />;
}

export { ExistingApplicationGuard };
